import { useState, useRef, useEffect, useCallback } from 'react';
import { consumePrewarmStream } from './useMediaPrewarm';
import type { ConnectionStatus } from '../types';
import { showImportantToast } from '@/features/videocall/components/components/Videocalltoasts';
import { getPlatformInfo, getMediaConstraints } from '@/lib/platform';
import { SignalingService } from '@/features/videocall/services/signaling.service';
import { WebRTCService } from '@/features/videocall/services/webrtc.service';
import { MediasoupService } from '@/features/videocall/services/mediasoup.service';
import { VideoPlaybackService } from '@/features/videocall/services/video-playback.service';
import { CallStateMachine } from '@/lib/webrtc/state-machine';

const USE_MEDIASOUP = import.meta.env.VITE_USE_MEDIASOUP === 'true';

interface UseWebRTCProps {
  roomId: string;
  isHost: boolean;
  localVideoRef: React.RefObject<HTMLVideoElement>;
  remoteVideoRef: React.RefObject<HTMLVideoElement>;
  localStreamRef: React.MutableRefObject<MediaStream | null>;
  socketRef: React.MutableRefObject<any>;
  callStartRef: React.MutableRefObject<Date | null>;
  peerRef: React.MutableRefObject<RTCPeerConnection | null>;
  prejoinState: any;
  customerData?: any;
  BASE_URL: string;
  SIGNALING_URL: string;
  setIsMuted: (val: boolean) => void;
  setIsVideoOff: (val: boolean) => void;
  setRemoteConnected: (val: boolean) => void;
  setIsRecording?: (val: boolean) => void;
}

export function useWebRTC({
  roomId,
  isHost,
  localVideoRef,
  remoteVideoRef,
  localStreamRef,
  socketRef,
  callStartRef,
  peerRef,
  prejoinState,
  customerData,
  BASE_URL,
  SIGNALING_URL,
  setIsMuted,
  setIsVideoOff,
  setRemoteConnected,
  setIsRecording,
}: UseWebRTCProps) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>("idle");
  const [remoteConnectedState, setRemoteConnectedState] = useState(false);
  const [remoteVideoEnabled, setRemoteVideoEnabled] = useState(true);
  const [waitingForHost, setWaitingForHost] = useState(!isHost);

  // Service instances (webrtcServiceRef holds WebRTCService or MediasoupService when USE_MEDIASOUP)
  const signalingServiceRef = useRef<SignalingService | null>(null);
  const webrtcServiceRef = useRef<WebRTCService | MediasoupService | null>(null);
  const mediasoupServiceRef = useRef<MediasoupService | null>(null);
  const videoPlaybackServiceRef = useRef<VideoPlaybackService | null>(null);
  const stateMachineRef = useRef<CallStateMachine | null>(null);

  // ✅ iOS Audio Fix: AudioContext for reliable remote audio playback
  // iOS Safari blocks <video> element audio due to autoplay policy & silent switch.
  // Routing remote audio through AudioContext bypasses both issues.
  // Created eagerly (will be "suspended" on iOS), then resumed on first user tap.
  const remoteAudioCtxRef = useRef<AudioContext | null>(null);
  const remoteAudioSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const remoteAudioGainRef = useRef<GainNode | null>(null); // controls remote audio volume (0 = muted for translation)
  const remoteAudioMutedRef = useRef<boolean>(false); // desired mute state — applied when GainNode is created

  // Platform info
  const platform = getPlatformInfo();

  // Helper to look at dimensions
  function logTrack(label: string, track?: MediaStreamTrack | null) {
    if (!track) {
      console.log(`[${label}] no track`);
      return;
    }
    const settings = track.getSettings?.();
    const constraints = track.getConstraints?.();
    console.log(`[${label}] id=${track.id} kind=${track.kind} enabled=${track.enabled} readyState=${track.readyState}`);
    console.log(`[${label}] settings=`, settings);
    console.log(`[${label}] constraints=`, constraints);
    }


  // Create AudioContext eagerly on mount (suspended on iOS until user gesture)
  if (!remoteAudioCtxRef.current) {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      remoteAudioCtxRef.current = new AudioCtx();
      console.log('🔊 [iOS Audio] AudioContext created on init, state:', remoteAudioCtxRef.current.state);
    } catch (e) {
      console.warn('⚠️ [iOS Audio] Failed to create AudioContext:', e);
    }
  }

  const startedRef = useRef(false);
  const didStartRef = useRef(false);
  const previousRemoteVideoTrackIdRef = useRef<string | null>(null);

  // Perfect Negotiation state
  const makingOfferRef = useRef(false);
  const hasPeerRef = useRef(false);

  const cleanupRemote = useCallback(() => {
    console.log('🧹 Cleaning up remote video');
    const el = remoteVideoRef.current;
    if (!el) return;

    try { el.pause(); } catch {}
    el.srcObject = null;
    try { el.removeAttribute('src'); } catch {}
    try { el.load(); } catch {}

    // ✅ REJOIN FIX: Reset track ID so sync monitor detects new track on rejoin
    previousRemoteVideoTrackIdRef.current = null;

    setRemoteConnectedState(false);
    setRemoteConnected(false);
  }, [remoteVideoRef, setRemoteConnected]);

  // Optimized getUserMedia with fallbacks
  async function getUserMediaOptimized(constraints: MediaStreamConstraints): Promise<MediaStream> {
    console.log('🔍 getUserMedia - Environment:', { platform });

    if (platform.isWebView) {
      console.log('📱 Using WebView-optimized constraints');
      const webViewConstraints = getMediaConstraints(platform, isHost ? 'host' : 'guest');

      try {
        const stream = await navigator.mediaDevices.getUserMedia(webViewConstraints);
        console.log('✅ WebView stream acquired:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
        return stream;
      } catch (err: any) {
        console.warn('⚠️ WebView optimized failed, trying basic...', err);
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: isHost ? 'user' : 'environment', width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 30, max: 60 } },
            audio: true
          });
          console.log('✅ WebView basic stream acquired');
          return stream;
        } catch (fallbackErr) {
          console.error('❌ WebView basic failed:', fallbackErr);
          throw fallbackErr;
        }
      }
    }

    // Regular web browser
    try {
      const stream = await consumePrewarmStream(constraints);
      console.log('✅ Stream acquired:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
      return stream;
    } catch (err: any) {
      console.warn('⚠️ Primary getUserMedia failed, trying fallback...', err);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280, max: 1920 }, height: { ideal: 720, max: 1080 }, frameRate: { ideal: 30, max: 60 } }, audio: true });
        console.log('✅ Fallback stream acquired');
        return stream;
      } catch (fallbackErr) {
        console.error('❌ All getUserMedia attempts failed:', fallbackErr);
        throw fallbackErr;
      }
    }
  }

  const startCall = useCallback(async () => {
    if (startedRef.current) {
      console.log('⏭️ Call already started, skipping');
      return;
    }
    startedRef.current = true;

    try {
      if (!signalingServiceRef.current) {
        throw new Error('Signaling service not initialized');
      }

      setConnectionStatus('connecting');
      console.log('🎬 🎬 🎬 STARTING CALL 🎬 🎬 🎬');
      console.log('Room ID:', roomId);
      console.log('Is Host:', isHost);

      // Build constraints — use ideal for resolution and aspectRatio to avoid strict crop on flip
      const baseVideoConstraints = {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 30, max: 60 },
        aspectRatio: { ideal: 16 / 9 }, // ideal only; do not use exact (causes crop on mobile after flip)
      };
      const videoConstraints: MediaTrackConstraints = prejoinState?.videoDeviceId
        ? { deviceId: { exact: prejoinState.videoDeviceId }, ...baseVideoConstraints }
        : prejoinState?.facingMode
          ? { facingMode: { exact: prejoinState.facingMode }, ...baseVideoConstraints }
          : { facingMode: 'user', ...baseVideoConstraints };

      // ✅ FIX: Add explicit audio quality settings for better transcription
      const audioConstraints: boolean | MediaTrackConstraints = prejoinState?.audioDeviceId
        ? {
            deviceId: { exact: prejoinState.audioDeviceId },
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,        // 48 kHz for high quality audio
            channelCount: 1,          // Mono (sufficient for speech, reduces bandwidth)
          }
        : {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000,        // 48 kHz for high quality audio
            channelCount: 1,          // Mono (sufficient for speech, reduces bandwidth)
          };

      const constraints: MediaStreamConstraints = {
        video: videoConstraints,
        audio: audioConstraints,
      };

      console.log('🎥 Getting media stream with constraints:', constraints);
      const stream = await getUserMediaOptimized(constraints);
      console.log('✅ Local stream acquired:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
      logTrack('local/video (after getUserMedia)', stream.getVideoTracks()[0]);
      logTrack('local/audio (after getUserMedia)', stream.getAudioTracks()[0]);


      const localVt = stream.getVideoTracks()[0];
      // console.log('[local track settings]', localVt?.getSettings?.());
      // if (localVt) {
      //   console.log('[local track] constraints=', localVt.getConstraints());
      //   console.log('[local track] settings=', localVt.getSettings());
      // }


      // Set up local video with VideoPlaybackService
      if (localVideoRef.current && videoPlaybackServiceRef.current) {
        console.log('📹 Setting local video element');
        await videoPlaybackServiceRef.current.playVideo(
          localVideoRef.current,
          stream,
          { platform, isMuted: true }
        );
        console.log('✅ Local video playing');
      }
      localStreamRef.current = stream;

      // Apply prejoin settings
      if (prejoinState) {
        if (typeof prejoinState.micOn === 'boolean') {
          stream.getAudioTracks().forEach(t => {
            t.enabled = prejoinState.micOn;
            console.log(`🎤 Mic ${prejoinState.micOn ? 'enabled' : 'disabled'}`);
          });
          setIsMuted(!prejoinState.micOn);
        }
        if (typeof prejoinState.camOn === 'boolean') {
          stream.getVideoTracks().forEach(t => {
            t.enabled = prejoinState.camOn;
            console.log(`📹 Camera ${prejoinState.camOn ? 'enabled' : 'disabled'}`);
          });
          setIsVideoOff(!prejoinState.camOn);
        }
      }

      // ========== MEDIASOUP SFU FLOW ==========
      if (USE_MEDIASOUP && mediasoupServiceRef.current && socketRef.current && roomId) {
        console.log('🎬 [mediasoup] Starting mediasoup SFU flow');
        const ms = mediasoupServiceRef.current;
        ms.setSocket(socketRef.current);
        ms.setRoomId(roomId);
        const caps = await new Promise<any>((res, rej) => {
          socketRef.current!.emit('mediasoup-get-router-capabilities', { roomId }, (r: any) => {
            if (r?.error) rej(new Error(r.error));
            else res(r);
          });
        });
        await ms.loadDevice(caps.rtpCapabilities);
        await ms.createSendTransport();
        await ms.createRecvTransport();
        for (const track of stream.getTracks()) {
          console.log('[useWebRTC] about to produce local video track', track?.getSettings?.());
          await ms.produceTrack(track);
        }
        let applyingRemoteStream = false;
        let queuedRemoteStream: MediaStream | null = null;
        const applyRemoteStream = async (remoteStream: MediaStream) => {
          // Guard against overlapping calls (audio+video producers arriving concurrently)
          if (applyingRemoteStream) {
            console.log('[mediasoup] applyRemoteStream already running, queueing latest stream');
            queuedRemoteStream = remoteStream;
            return;
          }
          applyingRemoteStream = true;
          try {
            setRemoteConnectedState(true);
            setRemoteConnected(true);
            setConnectionStatus('connected');
            if (!isHost) setWaitingForHost(false);

            // Route remote audio through AudioContext (same as P2P flow)
            // Re-route every time: handles late-arriving audio tracks
            const audioTracks = remoteStream.getAudioTracks();
            console.log(`[mediasoup] Remote stream has ${audioTracks.length} audio track(s), ${remoteStream.getVideoTracks().length} video track(s)`);
            // Route audio through GainNode on ALL platforms — required for translation mute control.
            if (audioTracks.length > 0 && remoteAudioCtxRef.current) {
              try {
                if (remoteAudioSourceRef.current) {
                  try { remoteAudioSourceRef.current.disconnect(); } catch {}
                }
                remoteAudioSourceRef.current = remoteAudioCtxRef.current.createMediaStreamSource(remoteStream);
                if (!remoteAudioGainRef.current || remoteAudioGainRef.current.context !== remoteAudioCtxRef.current) {
                  remoteAudioGainRef.current = remoteAudioCtxRef.current.createGain();
                  remoteAudioGainRef.current.connect(remoteAudioCtxRef.current.destination);
                }
                // Apply any pending mute state (translation may have been enabled before stream arrived)
                remoteAudioGainRef.current.gain.value = remoteAudioMutedRef.current ? 0 : 1;
                remoteAudioSourceRef.current.connect(remoteAudioGainRef.current);
                console.log('[mediasoup] Remote audio routed through AudioContext, state:', remoteAudioCtxRef.current.state);
                if (remoteAudioCtxRef.current.state === 'suspended') {
                  remoteAudioCtxRef.current.resume().catch(() => {});
                }
              } catch (e) {
                console.warn('[mediasoup] Failed to route remote audio:', e);
              }
            }

            if (remoteVideoRef.current) {
              const videoEl = remoteVideoRef.current;

              // Only set srcObject if it changed (getRemoteStream reuses the same object now)
              if (videoEl.srcObject !== remoteStream) {
                console.log('[mediasoup] Setting new remote stream on video element');
                // Always mute the video element — audio is routed through AudioContext/GainNode.
                // Do NOT condition on audioCtxRunning: even if suspended, we don't want the
                // video element playing audio directly (it bypasses the GainNode mute control).
                videoEl.muted = !!remoteAudioCtxRef.current;
                videoEl.playsInline = true;
                videoEl.setAttribute('playsinline', 'true');
                videoEl.autoplay = true;
                videoEl.srcObject = remoteStream;
              }

              // Ensure video is playing
              if (videoEl.paused && remoteStream.getVideoTracks().length > 0) {
                try {
                  await videoEl.play();
                  console.log('[mediasoup] Remote video playing');
                } catch (e: any) {
                  console.warn('[mediasoup] Play failed:', e.message);
                  // Muted fallback for autoplay policy
                  if (!videoEl.muted) {
                    try {
                      videoEl.muted = true;
                      await videoEl.play();
                      console.log('[mediasoup] Remote video playing (muted fallback)');
                    } catch {}
                  }
                }
              }

              const vt = remoteStream.getVideoTracks()[0];
              console.log('[remote track settings]', vt?.getSettings?.());
              if (vt && vt.id !== previousRemoteVideoTrackIdRef.current) {
                previousRemoteVideoTrackIdRef.current = vt.id;
                console.log('[remote track changed] settings=', vt.getSettings());
              }
              if (vt) setRemoteVideoEnabled(vt.enabled);
            }
          } finally {
            applyingRemoteStream = false;
            // If another producer update arrived while we were applying, immediately apply latest stream
            if (queuedRemoteStream) {
              const nextStream = queuedRemoteStream;
              queuedRemoteStream = null;
              setTimeout(() => {
                void applyRemoteStream(nextStream);
              }, 0);
            } else {
              queuedRemoteStream = null;
            }
          }
        };
        const producers = await new Promise<any[]>((res) => {
          socketRef.current!.emit('mediasoup-get-producers', { roomId }, (r: any) => {
            res(r?.producers || []);
          });
        });
        for (const p of producers) {
          try {
            await ms.consume(p.producerId);
          } catch (e) {
            console.warn('[mediasoup] Consume failed:', e);
          }
        }
        const remoteStream = ms.getRemoteStream();
        if (remoteStream && remoteStream.getTracks().length > 0) {
          await applyRemoteStream(remoteStream);
        }
        let applyRemoteTimeout: ReturnType<typeof setTimeout> | null = null;
        socketRef.current!.on('mediasoup-new-producer', async (data: { roomId: string; producerId: string; socketId: string; kind: string }) => {
          if (data.roomId !== roomId) return;
          try {
            await ms.consume(data.producerId);
            // Debounce: wait for audio+video to arrive before applying (avoids AbortError from rapid srcObject updates)
            if (applyRemoteTimeout) clearTimeout(applyRemoteTimeout);
            applyRemoteTimeout = setTimeout(async () => {
              applyRemoteTimeout = null;
              const rs = ms.getRemoteStream();
              if (rs && remoteVideoRef.current) await applyRemoteStream(rs);
            }, 150);
          } catch (e) {
            console.warn('[mediasoup] New producer consume failed:', e);
          }
        });
        if (prejoinState) {
          if (typeof prejoinState.micOn === 'boolean') ms.setMuted(!prejoinState.micOn);
          if (typeof prejoinState.camOn === 'boolean') ms.setVideoEnabled(prejoinState.camOn);
        }
        callStartRef.current = new Date();
        setConnectionStatus('connected');
        stateMachineRef.current?.transition({ type: 'START_CALL', platform, role: isHost ? 'host' : 'guest' });
        if (isHost && roomId) sessionStorage.setItem(`joined_${roomId}`, 'true');
        console.log('✅ [mediasoup] SFU flow complete');
        return;
      }

      // ========== P2P WEBRTC FLOW ==========
      // ✅ FIX: Wait for peer connection to be created before adding stream
      // The peer connection is created asynchronously (fetching TURN tokens)
      console.log('⏳ Waiting for peer connection to be ready...');
      let retries = 0;
      const maxRetries = 50; // 5 seconds max wait
      while (!peerRef.current && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!peerRef.current) {
        throw new Error('Peer connection not ready after 5 seconds');
      }

      // Add stream to WebRTC service
      if (webrtcServiceRef.current) {
        console.log('🔗 Adding local stream to peer connection');
        (webrtcServiceRef.current as WebRTCService).addLocalStream(stream);
        // Sync mute state so toggle works correctly (prejoin already set track.enabled)
        const initialMuted = typeof prejoinState?.micOn === 'boolean' ? !prejoinState.micOn : false;
        webrtcServiceRef.current.setMuted(initialMuted);
      }

      callStartRef.current = new Date();
      setConnectionStatus("connecting");

      // Transition state machine to waiting-for-peer
      stateMachineRef.current?.transition({
        type: 'START_CALL',
        platform,
        role: isHost ? 'host' : 'guest'
      });

      console.log('✅ ✅ ✅ CALL STARTED SUCCESSFULLY ✅ ✅ ✅');

      // Note: Initial video/audio state is emitted in onPeerJoined handler
      // to ensure the other peer's listeners are ready

      // ✅ Mark that host has joined this room (for rejoin detection)
      if (isHost && roomId) {
        sessionStorage.setItem(`joined_${roomId}`, 'true');
      }

    } catch (err: any) {
      console.error('❌ ❌ ❌ START CALL FAILED ❌ ❌ ❌');
      console.error('Error:', err);

      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setConnectionStatus("denied");
      } else if (err.name === "NotFoundError") {
        setConnectionStatus("failed");
      } else {
        setConnectionStatus("failed");
      }
    }
  }, [
    roomId,
    isHost,
    prejoinState,
    localVideoRef,
    localStreamRef,
    callStartRef,
    setIsMuted,
    setIsVideoOff,
    platform,
  ]);

  // Initialize services and socket
  useEffect(() => {
    if (!roomId) return;

    console.log('🔌 🔌 🔌 INITIALIZING SERVICES 🔌 🔌 🔌');

    // Initialize state machine
    stateMachineRef.current = new CallStateMachine();

    // Initialize services
    signalingServiceRef.current = new SignalingService({ url: SIGNALING_URL });
    signalingServiceRef.current.setStateMachine(stateMachineRef.current);
    if (USE_MEDIASOUP) {
      mediasoupServiceRef.current = new MediasoupService();
      webrtcServiceRef.current = mediasoupServiceRef.current;
    } else {
      webrtcServiceRef.current = new WebRTCService(platform);
    }
    videoPlaybackServiceRef.current = new VideoPlaybackService();

    const signalingService = signalingServiceRef.current;
    const webrtcService = webrtcServiceRef.current;
    const videoPlaybackService = videoPlaybackServiceRef.current;

    // ✅ CRITICAL FIX: Create a Promise to track when peer connection is ready
    // This prevents race condition where offer arrives before peer connection is ready
    let peerConnectionReadyResolve: () => void;
    const peerConnectionReady = new Promise<void>((resolve) => {
      peerConnectionReadyResolve = resolve;
    });

    // ✅ CRITICAL FIX: Initialize peer connection BEFORE setting up event listeners
    // This prevents race condition where offer arrives before peer connection is ready
    (async () => {
      if (USE_MEDIASOUP) {
        peerConnectionReadyResolve();
        console.log('✅ [mediasoup] Skipping P2P - using SFU');
        return;
      }
      console.log('⏳ Creating peer connection (fetching TURN tokens)...');
      const pc = await (webrtcService as WebRTCService).createPeerConnection();
      peerRef.current = pc;
      console.log('✅ Peer connection created and ready');

      // Signal that peer connection is ready
      peerConnectionReadyResolve();

      // Set up WebRTC event handlers AFTER peer connection is created (P2P only)
      if (USE_MEDIASOUP) return;
      (webrtcService as WebRTCService).onTrack(async (stream) => {
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();

      console.log('📹 [TRACK] REMOTE TRACK RECEIVED:', {
        videoTracks: videoTracks.length,
        audioTracks: audioTracks.length,
        streamId: stream.id,
        videoTrackId: videoTracks[0]?.id,
        videoEnabled: videoTracks[0]?.enabled,
        videoReadyState: videoTracks[0]?.readyState,
        audioEnabled: audioTracks[0]?.enabled
      });

      // Mark remote connected IMMEDIATELY
      console.log("🟢 [TRACK] Marking remoteConnected = true");
      setRemoteConnectedState(true);
      setRemoteConnected(true);
      setConnectionStatus("connected");

      // ✅ CRITICAL FIX: Clear waiting screen when remote track received (guest sees host's video)
      if (!isHost) {
        console.log('✅ [TRACK] Guest received remote track - clearing waiting screen');
        setWaitingForHost(false);
      }

      // ✅ iOS Audio Fix: Route remote audio through AudioContext for reliable playback.
      // This runs for EVERY track event (audio or video) since the stream may contain audio.
      if (audioTracks.length > 0 && remoteAudioCtxRef.current) {
        try {
          // Disconnect previous source if any
          if (remoteAudioSourceRef.current) {
            try { remoteAudioSourceRef.current.disconnect(); } catch {}
          }
          // Connect remote audio stream to speakers via AudioContext (via GainNode for mute control)
          remoteAudioSourceRef.current = remoteAudioCtxRef.current.createMediaStreamSource(stream);
          if (!remoteAudioGainRef.current || remoteAudioGainRef.current.context !== remoteAudioCtxRef.current) {
            remoteAudioGainRef.current = remoteAudioCtxRef.current.createGain();
            remoteAudioGainRef.current.connect(remoteAudioCtxRef.current.destination);
          }
          // Apply any pending mute state (translation may have been enabled before stream arrived)
          remoteAudioGainRef.current.gain.value = remoteAudioMutedRef.current ? 0 : 1;
          remoteAudioSourceRef.current.connect(remoteAudioGainRef.current);
          console.log('🔊 [iOS Audio] Remote audio routed through AudioContext, state:', remoteAudioCtxRef.current.state);

          // If AudioContext is suspended (iOS — no user gesture yet), try to resume
          if (remoteAudioCtxRef.current.state === 'suspended') {
            console.log('🔊 [iOS Audio] AudioContext suspended — attempting resume');
            remoteAudioCtxRef.current.resume().catch(() => {});
          }
        } catch (e) {
          console.warn('⚠️ [iOS Audio] Failed to route remote audio:', e);
        }
      }

      // Set remote video stream - ONLY for streams with video tracks
      // Skip audio-only track events to avoid race conditions
      if (videoTracks.length === 0) {
        console.log('📢 [TRACK] Audio-only track received, skipping video element update');
        return;
      }

      if (remoteVideoRef.current) {
        console.log('🔄 [TRACK] Setting remote video stream to video element', {
          streamId: stream.id,
          trackCount: stream.getTracks().length,
          videoTrackId: videoTracks[0]?.id,
          videoTrackLabel: videoTracks[0]?.label,
          audioTracks: audioTracks.length
        });

        const videoEl = remoteVideoRef.current;

        // ✅ CRITICAL FIX: Force full video element reset to ensure proper video refresh
        // This handles the case when host joins first, then guest joins OR when peer rejoins
        console.log('🔄 [TRACK] Forcing complete video element reset');
        videoEl.pause();
        videoEl.srcObject = null;
        // NOTE: Do NOT call videoEl.load() - it's for <video src="url">, not srcObject!
        // Calling load() on a MediaStream can interfere with playback.

        // ✅ REJOIN FIX: Reset previous track ID so sync monitor detects the new track
        previousRemoteVideoTrackIdRef.current = null;

        // Small delay to let browser process the clear
        await new Promise(resolve => setTimeout(resolve, 100));

        // ✅ CRITICAL FIX: Set video element properties BEFORE srcObject
        // Mute the <video> element — audio is routed through AudioContext instead
        // (more reliable on iOS, bypasses silent switch and autoplay restrictions)
        videoEl.muted = !!remoteAudioCtxRef.current; // muted if AudioContext handles audio
        videoEl.playsInline = true;
        videoEl.autoplay = true; // Enable autoplay BEFORE srcObject

        // Now set the new stream
        videoEl.srcObject = stream;

        console.log('📺 [TRACK] Video element state after setting srcObject:', {
          paused: videoEl.paused,
          readyState: videoEl.readyState,
          networkState: videoEl.networkState,
          hasSrcObject: !!videoEl.srcObject,
          autoplay: videoEl.autoplay,
          playsInline: videoEl.playsInline
        });

        // ✅ CRITICAL FIX: With autoplay set BEFORE srcObject, video should play automatically
        // Wait briefly for tracks to be ready, but don't block on metadata events
        // Many browsers with autoplay enabled will start playing without firing loadedmetadata
        await new Promise(resolve => setTimeout(resolve, 300));

        // Try to play with retry logic (up to 3 attempts)
        let playAttempts = 0;
        const maxPlayAttempts = 3;
        let playSuccess = false;

        while (playAttempts < maxPlayAttempts && !playSuccess) {
          playAttempts++;

          try {
            // Verify srcObject is still set (browser sometimes clears it)
            if (!videoEl.srcObject) {
              console.warn(`⚠️ [TRACK] srcObject was cleared, restoring (attempt ${playAttempts})`);
              videoEl.srcObject = stream;
              await new Promise(resolve => setTimeout(resolve, 100));
            }

            await videoEl.play();
            console.log(`✅ [TRACK] Remote video auto-playing successfully (attempt ${playAttempts})`);
            console.log('📺 [TRACK] Video element state after play:', {
              paused: videoEl.paused,
              readyState: videoEl.readyState,
              videoWidth: videoEl.videoWidth,
              videoHeight: videoEl.videoHeight
            });

            playSuccess = true;
          } catch (err: any) {
            console.error(`❌ [TRACK] Play attempt ${playAttempts} failed:`, err.message);

            if (playAttempts < maxPlayAttempts) {
              console.log(`🔄 [TRACK] Retrying play in 500ms (attempt ${playAttempts + 1}/${maxPlayAttempts})`);

              // ✅ CRITICAL FIX: On final retry, force complete video element reset
              if (playAttempts === 2) {
                console.log('🔧 [TRACK] Final attempt - forcing complete video reset');
                videoEl.pause();
                videoEl.srcObject = null;
                await new Promise(resolve => setTimeout(resolve, 200));
                // Re-set properties before srcObject
                videoEl.muted = !!remoteAudioCtxRef.current;
                videoEl.playsInline = true;
                videoEl.autoplay = true;
                videoEl.srcObject = stream;
                await new Promise(resolve => setTimeout(resolve, 300));
              } else {
                await new Promise(resolve => setTimeout(resolve, 500));
              }
            } else {
              console.error('❌ [TRACK] All play attempts failed - showing tap prompt');
              console.log('📺 [TRACK] Stream info when autoplay failed:', {
                streamId: stream.id,
                videoTrackId: videoTracks[0]?.id,
                videoTrackLabel: videoTracks[0]?.label,
                videoTrackEnabled: videoTracks[0]?.enabled,
                videoTrackReadyState: videoTracks[0]?.readyState,
                videoElementReadyState: videoEl.readyState
              });
            }
          }
        }
      } else {
        console.error('❌ [TRACK] No remoteVideoRef.current available!');
      }

      // Set initial remote video state
      if (videoTracks.length > 0) {
        setRemoteVideoEnabled(videoTracks[0].enabled);
        console.log('📹 [TRACK] Remote video track initial state:', {
          trackId: videoTracks[0].id,
          enabled: videoTracks[0].enabled,
          readyState: videoTracks[0].readyState,
          muted: videoTracks[0].muted
        });
      }
      });

      (webrtcService as WebRTCService).onIceCandidate((candidate) => {
        console.log('🧊 Sending ICE candidate');
        signalingService.sendIceCandidate(candidate);
      });

      (webrtcService as WebRTCService).onConnectionStateChange(async (state) => {
        console.log('🔌 Peer connection state:', state);
        switch (state) {
          case 'connected':
            setConnectionStatus('connected');
            console.log('✅ ✅ ✅ PEER CONNECTION ESTABLISHED ✅ ✅ ✅');

            // Log connection statistics to verify TURN usage
            setTimeout(async () => {
              const stats = await (webrtcService as WebRTCService).getStats();
              if (stats) {
                stats.forEach((report: any) => {
                  if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                    console.log('📊 [CONNECTION STATS]', {
                      localCandidateType: report.localCandidateType,
                      remoteCandidateType: report.remoteCandidateType,
                      usingTURN: report.localCandidateType === 'relay' || report.remoteCandidateType === 'relay',
                      protocol: report.currentRoundTripTime ? 'active' : 'unknown',
                      bytesReceived: report.bytesReceived,
                      bytesSent: report.bytesSent,
                      roundTripTime: report.currentRoundTripTime,
                    });

                    if (report.localCandidateType === 'relay' || report.remoteCandidateType === 'relay') {
                      console.log('🎯🎯🎯 TURN RELAY ACTIVE - Mobile data mode confirmed! 🎯🎯🎯');
                    }
                  }
                });
              }
            }, 2000);

            // ✅ CRITICAL FIX: Force video refresh when connection is established
            // This ensures video displays even if ontrack fired before video elements were ready
            setTimeout(async () => {
              const remoteStream = webrtcService.getRemoteStream();
              if (remoteStream && remoteVideoRef.current && videoPlaybackService) {
                console.log('🔄 Force refreshing remote video on connection');
                try {
                  await videoPlaybackService.refreshStream(
                    remoteVideoRef.current,
                    remoteStream,
                    platform
                  );
                  console.log('✅ Remote video force refreshed successfully');
                } catch (err) {
                  console.warn('⚠️ Failed to force refresh remote video:', err);
                }
              }
            }, 500); // Small delay to ensure everything is ready
            break;
          case 'disconnected':
          case 'failed':
            setConnectionStatus('reconnecting');
            console.warn('⚠️ Connection lost, attempting to reconnect...');
            break;
          case 'closed':
            cleanupRemote();
            console.log('🔒 Connection closed');
            break;
        }
      });

      // ========================================
      // Perfect Negotiation: Negotiation Needed
      // ========================================
      (webrtcService as WebRTCService).onNegotiationNeeded(async () => {
        console.log('\n' + '='.repeat(60));
        console.log('🔔 NEGOTIATION NEEDED (Perfect Negotiation)');
        console.log('='.repeat(60));
        console.log('I am:', isPolite ? 'POLITE (host)' : 'IMPOLITE (guest)');
        console.log('Has peer:', hasPeerRef.current);
        console.log('Current PC state:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState);
        console.log('makingOffer:', makingOfferRef.current);
        console.log('Timestamp:', new Date().toISOString());
        console.log('='.repeat(60));

        // Don't send offer if we don't have a peer yet (host waiting alone)
        if (!hasPeerRef.current) {
          console.log('⏭️ No peer yet, skipping offer creation');
          return;
        }

        // ✅ FIX: Add small delay for polite peer to reduce collision chance
        // Impolite peer (guest) sends offer immediately, polite peer (host) waits a bit
        if (isPolite) {
          console.log('⏳ Polite peer waiting 300ms to reduce collision...');
          await new Promise(resolve => setTimeout(resolve, 300));

          // Check if we're still in stable state (no collision happened)
          const currentState = (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState;
          if (currentState !== 'stable') {
            console.log(`⏭️ Not in stable state (${currentState}), skipping offer (collision handled)`);
            return;
          }
        }

        try {
          makingOfferRef.current = true;

          console.log('📤 Creating offer...');
          const offer = await (webrtcServiceRef.current as WebRTCService).createOfferPerfectNegotiation();
          console.log('✅ Offer created successfully');
          console.log('   Type:', offer.type);
          console.log('   SDP length:', offer.sdp?.length || 0);

          console.log('✅ Offer created, sending to peer');
          signalingServiceRef.current?.sendOffer(offer);

          stateMachineRef.current?.transition({ type: 'OFFER_SENT' });

        } catch (error: any) {
          console.error('❌ Failed to send offer:', error);
        } finally {
          makingOfferRef.current = false;
        }
      });

      console.log('✅ Peer connection and event handlers initialized');
    })();

    // ✅ CRITICAL FIX: Set up ALL signaling event handlers BEFORE connecting
    // This ensures we don't miss any events that fire immediately upon joining
    console.log('🎧 Setting up event listeners BEFORE connecting...');

    // ========================================
    // Perfect Negotiation: Peer Joined (SYMMETRIC)
    // ========================================
    const cleanupPeerJoined = signalingService.onPeerJoined(async (peerId) => {
      console.log('\n' + '='.repeat(60));
      console.log('🎉 PEER JOINED (Perfect Negotiation)');
      console.log('='.repeat(60));
      console.log('Peer:', peerId);
      console.log('I am:', isHost ? 'HOST (polite)' : 'GUEST (impolite)');
      console.log('Room ID:', roomId);
      console.log('='.repeat(60) + '\n');

      const peerName = isHost
        ? (customerData?.name || "Guest")
        : "Host";

      showImportantToast(`${peerName} joined the call`, "peer-joined");

      // Clear waiting screen
      if (!isHost) {
        setWaitingForHost(false);
        setConnectionStatus("connecting");
      }

      // Mark that we have a peer
      hasPeerRef.current = true;

      // Update state machine
      stateMachineRef.current?.transition({ type: 'PEER_JOINED', socketId: peerId });

      // CRITICAL: Check if call was already started BEFORE we try to start it again
      // This captures the state before any modifications
      const callAlreadyStarted = startedRef.current;

      // Perfect Negotiation: Both peers start call symmetrically
      let callStartedSuccessfully = false;
      if (!startedRef.current) {
        console.log('🎬 Starting call (symmetric - both peers)');

        try {
          await startCall();
          console.log('✅ Call started successfully');
          callStartedSuccessfully = true;
        } catch (error: any) {
          console.warn('⚠️ Error starting call (may continue without media):', error.message);
          console.warn('⚠️ Will manually create offer since negotiationneeded won\'t fire without tracks');
          // Mark as started so we don't try again
          startedRef.current = true;
        }
      }

      // If call was already started (host-first scenario), manually create offer now
      // OR if call start failed (no media), manually create offer
      // Skip for mediasoup - no offer/answer flow
      if (!USE_MEDIASOUP && (callAlreadyStarted || (!callStartedSuccessfully && startedRef.current))) {
        if (callAlreadyStarted) {
          console.log('🔄 Call already started, manually creating offer for new peer');
        } else {
          console.log('🔄 Creating offer manually (no media tracks, negotiationneeded won\'t fire)');
        }

        // ✅ REJOIN FIX: Wait for tracks to be fully ready before creating offer
        // This prevents empty offers or missing m-lines in SDP
        console.log('⏳ Verifying tracks are ready before creating offer...');
        const maxWaitTime = 3000; // Max 3 seconds
        const startWait = Date.now();

        while (Date.now() - startWait < maxWaitTime) {
          const senders = peerRef.current?.getSenders() || [];
          const hasVideo = senders.some(s => s.track?.kind === 'video' && s.track?.readyState === 'live');
          const hasAudio = senders.some(s => s.track?.kind === 'audio' && s.track?.readyState === 'live');

          if (hasVideo && hasAudio) {
            console.log('✅ Tracks ready: video and audio both present');
            break;
          }

          if (hasVideo || hasAudio) {
            console.log('⏳ Partial tracks ready, waiting for all... video:', hasVideo, 'audio:', hasAudio);
          }

          await new Promise(resolve => setTimeout(resolve, 50));
        }

        if (Date.now() - startWait >= maxWaitTime) {
          console.warn('⚠️ Timeout waiting for tracks, proceeding anyway');
        }

        try {
          makingOfferRef.current = true;
          const w = webrtcServiceRef.current as WebRTCService;
          const offer = await w.createOfferPerfectNegotiation();
          console.log('✅ Offer created, sending to peer...');
          console.log('   Type:', offer.type);
          console.log('   SDP length:', offer.sdp?.length || 0);
          signalingServiceRef.current?.sendOffer(offer);
          stateMachineRef.current?.transition({ type: 'OFFER_SENT' });
          console.log('✅ Offer sent successfully');
        } catch (error: any) {
          console.error('❌ Failed to create offer:', error);
        } finally {
          makingOfferRef.current = false;
        }
      } else {
        console.log('⏳ Waiting for negotiationneeded event...');
      }

      // ✅ Emit initial video/audio state to peer after peer joins
      // This ensures the other peer has their listeners set up
      setTimeout(() => {
        const socket = (signalingServiceRef.current as any)?.socket;
        if (socket?.connected && prejoinState) {
          if (typeof prejoinState.camOn === 'boolean') {
            const videoPayload = {
              roomId,
              enabled: prejoinState.camOn,
              senderId: socket.id,
            };
            console.log(`📡 [onPeerJoined] Emitting initial video-state:`, videoPayload);
            socket.emit('video-state', videoPayload);
          }
          if (typeof prejoinState.micOn === 'boolean') {
            const audioPayload = {
              roomId,
              enabled: prejoinState.micOn,
              senderId: socket.id,
            };
            console.log(`📡 [onPeerJoined] Emitting initial audio-state:`, audioPayload);
            socket.emit('audio-state', audioPayload);
          }
        }
      }, 500); // Small delay to ensure listeners are ready
    });

    // ========================================
    // Perfect Negotiation: Setup
    // ========================================
    const isPolite = isHost; // Host is polite peer

    // ========================================
    // Perfect Negotiation: Offer Received (P2P only - skip for mediasoup)
    // ========================================
    const noop = () => {};
    const cleanupOffer = USE_MEDIASOUP ? noop : signalingService.onOffer(async (offer) => {
      console.log('\n' + '='.repeat(60));
      console.log('📥 OFFER RECEIVED (Perfect Negotiation)');
      console.log('='.repeat(60));
      console.log('I am:', isHost ? 'HOST (polite)' : 'GUEST (impolite)');
      console.log('Offer type:', offer.type);
      console.log('SDP length:', offer.sdp?.length || 0);
      console.log('Current PC state:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState || 'none');
      console.log('makingOffer:', makingOfferRef.current);
      console.log('Has local stream:', !!localStreamRef.current);
      console.log('Timestamp:', new Date().toISOString());
      console.log('='.repeat(60) + '\n');

      // Guest: Clear waiting screen when offer arrives
      if (!isHost) {
        setWaitingForHost(false);
        setConnectionStatus("connecting");
      }

      // ✅ CRITICAL FIX: Wait for startCall to complete if in progress
      // This prevents double media acquisition
      if (!localStreamRef.current && startedRef.current) {
        console.log('⏳ startCall in progress, waiting for media to be ready...');
        const maxWait = 5000; // Max 5 seconds
        const startWait = Date.now();

        while (!localStreamRef.current && Date.now() - startWait < maxWait) {
          await new Promise(r => setTimeout(r, 50));
        }

        if (!localStreamRef.current) {
          console.warn('⚠️ Timeout waiting for startCall media, acquiring in offer handler...');
        }
      }

      // Ensure local stream (might not be ready yet if startCall never called)
      if (!localStreamRef.current) {
        console.log('⚠️ No local stream yet, acquiring...');
        try {
          const stream = await getUserMediaOptimized({
            video: true,
            audio: true,
          });

          if (localVideoRef.current && videoPlaybackService) {
            await videoPlaybackService.playVideo(
              localVideoRef.current,
              stream,
              { platform, isMuted: true }
            );
          }

          localStreamRef.current = stream;
          (webrtcServiceRef.current as WebRTCService).addLocalStream(stream);
          webrtcServiceRef.current!.setMuted(false); // Guest default: mic on
        } catch (error) {
          console.warn('⚠️ Could not acquire media, continuing without:', error);
        }
      }

      // Update state machine
      stateMachineRef.current?.transition({
        type: 'OFFER_RECEIVED',
        offer: offer as RTCSessionDescription
      });

      // Perfect Negotiation: Handle offer with collision detection
      console.log('🔧 Processing offer with collision detection...');
      console.log('   isPolite:', isPolite);
      console.log('   Current state before processing:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState);

      try {
        const answer = await (webrtcServiceRef.current as WebRTCService).handleOfferPerfectNegotiation(
          offer,
          isPolite
        );

        if (answer) {
          console.log('✅ Answer created successfully');
          console.log('   Answer type:', answer.type);
          console.log('   SDP length:', answer.sdp?.length || 0);
          console.log('   Sending to peer...');
          signalingServiceRef.current?.sendAnswer(answer);
          stateMachineRef.current?.transition({ type: 'ANSWER_SENT' });
          console.log('✅ Answer sent to signaling server');
        } else {
          console.log('🚫 Offer ignored due to collision (impolite peer)');
        }
      } catch (error: any) {
        const w = webrtcServiceRef.current as WebRTCService;
        console.error('❌ Failed to process offer:', error.message);
        console.error('   Signaling state:', w?.getPeerConnection()?.signalingState);
        console.error('   Connection state:', w?.getPeerConnection()?.connectionState);

        // If we're the impolite peer and failed, we should wait for polite peer's answer
        // If we're the polite peer and failed, this is a critical error
        if (isPolite) {
          // Error logged above
        } else {
          console.log('⏳ Impolite peer will wait for polite peer to process our offer');
        }
      }
    });

    // ========================================
    // Perfect Negotiation: Answer Received (P2P only)
    // ========================================
    const cleanupAnswer = USE_MEDIASOUP ? noop : signalingService.onAnswer(async (answer) => {
      console.log('\n' + '='.repeat(60));
      console.log('📥 ANSWER RECEIVED (Perfect Negotiation)');
      console.log('='.repeat(60));
      console.log('Answer type:', answer.type);
      console.log('SDP length:', answer.sdp?.length || 0);
      console.log('Current PC state:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState || 'none');
      console.log('Timestamp:', new Date().toISOString());
      console.log('='.repeat(60));

      try {
        console.log('🔧 Setting remote description (answer)...');
        await (webrtcServiceRef.current as WebRTCService).handleAnswer(answer);
        console.log('✅ Answer processed successfully');
        console.log('   New PC state:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState);

        stateMachineRef.current?.transition({ type: 'ANSWER_RECEIVED', answer: answer as RTCSessionDescription });

      } catch (error: any) {
        console.error('❌ Failed to handle answer:', error);
      }
    });

    const cleanupIceCandidate = USE_MEDIASOUP ? noop : signalingService.onIceCandidate(async (candidate) => {
      const pc = (webrtcServiceRef.current as WebRTCService)?.getPeerConnection();
      console.log('🧊 Received ICE candidate');
      console.log('   Type:', candidate.candidate?.split(' ')[7] || 'unknown');
      console.log('   Has remote desc:', !!pc?.remoteDescription);
      console.log('   PC state:', pc?.signalingState || 'none');
      await (webrtcService as WebRTCService).addIceCandidate(candidate);
    });

    // ========================================
    // Perfect Negotiation: Batched ICE Candidates
    // ========================================
    const cleanupIceBatch = USE_MEDIASOUP ? undefined : signalingService.onICECandidatesBatch?.(async (candidates) => {
      console.log('\n🚀 RECEIVED ICE BATCH:', candidates.length, 'candidates');

      for (const candidate of candidates) {
        try {
          await (webrtcServiceRef.current as WebRTCService).addIceCandidate(candidate);
        } catch (error) {
          console.warn('⚠️ Failed to add batched ICE candidate:', error);
        }
      }

      console.log('✅ Batched ICE candidates processed\n');
    });

    // ========================================
    // Perfect Negotiation: Collision Detection
    // ========================================
    const cleanupCollision = USE_MEDIASOUP ? undefined : signalingService.onOfferCollision?.(async (data) => {
      console.log('\n' + '='.repeat(60));
      console.log('🚨 OFFER COLLISION DETECTED');
      console.log('='.repeat(60));
      console.log('Should rollback:', data.shouldRollback);
      console.log('I am:', isPolite ? 'POLITE (host)' : 'IMPOLITE (guest)');
      console.log('Current PC state:', (webrtcServiceRef.current as WebRTCService)?.getPeerConnection()?.signalingState);
      console.log('='.repeat(60) + '\n');

      if (data.shouldRollback) {
        // ✅ FIX: Polite peer must explicitly rollback the pending offer
        console.log('🔄 Rolling back offer (polite peer)');
        makingOfferRef.current = false;

        // Explicitly rollback if still in have-local-offer state
        const pc = (webrtcServiceRef.current as WebRTCService)?.getPeerConnection();
        if (pc && pc.signalingState === 'have-local-offer') {
          try {
            console.log('  → Explicitly rolling back peer connection');
            await pc.setLocalDescription({ type: 'rollback' } as RTCSessionDescriptionInit);
            console.log('  → ✅ Rollback complete, now in stable state');
          } catch (error: any) {
            console.error('  → ❌ Rollback failed:', error.message);
            // Continue anyway - setRemoteDescription will handle implicit rollback
          }
        }
      } else {
        // Impolite peer ignored the offer - no action needed
        // The polite peer will accept impolite peer's offer and send answer
        console.log('⏳ Impolite peer waiting for polite peer to send answer...');
      }
    });

    const cleanupPeerLeft = signalingService.onPeerLeft((data) => {
      const peerData = typeof data === 'object' && data !== null ? data : { socketId: data, isHost: false };
      console.log('👋 Peer left the call:', peerData);

      // Reset peer flag
      hasPeerRef.current = false;

      // ✅ FIX: Don't show "peer left" toast if we already received "call-ended-by-host"
      // This prevents duplicate toasts when host ends the call
      if (signalingService.wasCallEndedByHost()) {
        console.log('⏭️ Skipping peer-left toast (already handled by call-ended-by-host)');
        cleanupRemote();
        setConnectionStatus("peer-left");
        return;
      }

      cleanupRemote();
      setConnectionStatus("peer-left");
    });

    const cleanupVideoState = signalingService.onVideoState((enabled) => {
      console.log('📡 Received peer video state:', enabled);
      setRemoteVideoEnabled(enabled);
    });

    // ✅ Register call-ended-by-host listener to set the flag in SignalingService
    const cleanupCallEndedByHost = signalingService.onCallEndedByHost(() => {
      console.log('🔴 Call ended by host event received in useWebRTC');
      // The flag is already set in SignalingService.onCallEndedByHost()
      // This listener is just to ensure the event is registered
    });

    // ✅ Listen for recording state changes from peer (shows toast to guest when host starts/stops recording)
    const cleanupRecordingState = signalingService.onRecordingState((isRecording) => {
      console.log('🎬 Recording state changed:', isRecording);

      // Update recording state for guests
      if (setIsRecording) {
        setIsRecording(isRecording);
      }

      // Show toast notification
      if (isRecording) {
        showImportantToast("This call is being recorded", "recording");
      }
    });

    // ✅ CRITICAL FIX: Wait for peer connection to be ready BEFORE connecting to signaling
    // This prevents race condition where offer arrives before peer connection exists
    console.log('⏳ Waiting for peer connection to be ready before connecting to signaling...');
    peerConnectionReady.then(() => {
      console.log('✅ Peer connection ready, now connecting to signaling server');
      console.log('🔌 Connecting to signaling server:', SIGNALING_URL);
      return signalingService.connect();
    }).then(async () => {
      console.log('✅ ✅ ✅ SIGNALING SERVICE CONNECTED ✅ ✅ ✅');

      // Store socket reference for backward compatibility
      socketRef.current = (signalingService as any).socket;

      setConnectionStatus("connecting");

      // ✅ NEW: Try to join room, handle "call ended" error
      try {
        await signalingService.joinRoom(roomId, isHost);
        console.log('🚪 Joined room:', roomId);
      } catch (error: any) {
        console.error('❌ Failed to join room:', error.message);

        // Navigate away after a short delay
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 2000);

        throw error; // Stop the promise chain
      }

      // Register participant if guest
      if (!isHost) {
        try {
          console.log('📝 Registering participant on server');
          const response = await fetch(`${BASE_URL}/api/join-call`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              roomId,
              participantId: (signalingService as any).socket.id,
            }),
          });
          const data = await response.json();
          console.log('📝 Join response:', data);
        } catch (error) {
          console.error("❌ join-call error:", error);
        }
      }

      // Host initialization - start local stream immediately so host sees their own video
      if (isHost) {
        console.log('🎬 Initializing as HOST...');
        console.log('🎥 Starting host local stream immediately (host will see own video while waiting)');

        // ✅ FIX: Start call immediately for host so they see their own video
        // WebRTC peer connection will still wait for guest to join (handled by peer-joined event)
        await startCall();
      } else if (!isHost) {
        // ✅ CRITICAL FIX: Check room state IMMEDIATELY after joining to prevent "waiting for host" when host is already there
        console.log('👤 Guest joined - checking if host is already present...');
        setConnectionStatus("idle");

        // ✅ Synchronous room state check using socket acknowledgment
        const socket = (signalingService as any).socket;
        if (socket) {
          console.log('🔍 Requesting current room state from server...');

          // Track if room state response was received
          let roomStateReceived = false;

          // Use callback in emit to get immediate response
          socket.emit('get-room-state', { roomId }, (response: { participants: any[], hosts: number, guests: number }) => {
            roomStateReceived = true;
            console.log('🔍 Room state response:', response);

            // Check if host is already in the room
            const hasHost = response?.hosts && response.hosts > 0;

            if (hasHost) {
              console.log('✅ Host already in room, clearing waiting screen and starting call immediately');
              setWaitingForHost(false);
              setConnectionStatus("connecting");

              // ✅ CRITICAL FIX: Start call immediately so media is ready BEFORE offer arrives
              // This prevents the race condition where offer handler tries to acquire media
              // Don't await here - let it run asynchronously to avoid blocking the callback
              if (!startedRef.current) {
                console.log('🎥 Starting guest call immediately (host present)');
                startCall().catch(err => {
                  console.error('❌ Failed to start call:', err);
                });
              }
            } else {
              console.log('⏳ Host not yet in room, showing waiting screen');
              setWaitingForHost(true);
            }
          });

          // ✅ FIX: Fallback timer - if get-room-state doesn't respond within 3 seconds,
          // start the call anyway. This handles new accounts or servers that don't support
          // the get-room-state callback.
          setTimeout(() => {
            if (!roomStateReceived && !startedRef.current) {
              console.warn('⚠️ [Guest] Room state check timed out after 3s - starting call as fallback');
              setWaitingForHost(false);
              setConnectionStatus("connecting");
              startCall().catch(err => {
                console.error('❌ Failed to start call (fallback):', err);
              });
            }
          }, 3000);
        }
      }
    });

    return () => {
      console.log('🧹 FINAL CLEANUP - Component unmounting');

      // ✅ CRITICAL: Stop local media tracks FIRST to release camera/mic permissions
      // This ensures the camera/mic indicator turns off immediately when the component unmounts
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log(`🛑 [Unmount] Stopped local ${track.kind} track`);
        });
        localStreamRef.current = null;
      }

      // Clear video element srcObjects to fully release media devices
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = null;
        console.log('🧹 [Unmount] Cleared local video srcObject');
      }
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
        console.log('🧹 [Unmount] Cleared remote video srcObject');
      }

      // ✅ iOS Audio Fix: Close AudioContext
      if (remoteAudioSourceRef.current) {
        try { remoteAudioSourceRef.current.disconnect(); } catch {}
        remoteAudioSourceRef.current = null;
      }
      if (remoteAudioCtxRef.current) {
        try { remoteAudioCtxRef.current.close(); } catch {}
        remoteAudioCtxRef.current = null;
        console.log('🧹 [Unmount] Closed remote AudioContext');
      }

      // Cleanup signaling event listeners
      cleanupPeerJoined();
      cleanupOffer();
      cleanupAnswer();
      cleanupIceCandidate();
      cleanupIceBatch?.();
      cleanupCollision?.();
      cleanupPeerLeft();
      cleanupVideoState();
      cleanupCallEndedByHost();
      cleanupRecordingState();

      // Cleanup services
      signalingService.cleanup();
      webrtcService.cleanup();

      // Clear refs
      signalingServiceRef.current = null;
      webrtcServiceRef.current = null;
      videoPlaybackServiceRef.current = null;
      stateMachineRef.current = null;
      socketRef.current = null;
      peerRef.current = null;
    };
  }, [roomId, isHost]); // Dependencies kept minimal

  // 🔄 CRITICAL FIX: Sync remote video element with actual stream
  // This handles replaceTrack() scenarios (e.g., screen sharing, camera switch)
  // replaceTrack() does NOT trigger ontrack event, so we need to poll for changes
  useEffect(() => {
    // Mediasoup is event-driven via mediasoup-new-producer/applyRemoteStream;
    // polling here can interfere with stream assignment and cause play() abort loops.
    if (USE_MEDIASOUP || !remoteConnectedState || !remoteVideoRef.current) return;

    console.log('🔄 Starting remote stream sync monitor');

    const syncInterval = setInterval(() => {
      const webrtcService = webrtcServiceRef.current;
      if (!webrtcService) return;

      const actualStream = webrtcService.getRemoteStream();
      const currentSrcObject = remoteVideoRef.current?.srcObject as MediaStream;
      const videoEl = remoteVideoRef.current;

      // Detailed status logging every 30 seconds (reduced from 10s to minimize log spam)
      const now = Date.now();
      if (!(window as any)._lastSyncLog || now - (window as any)._lastSyncLog > 30000) {
        (window as any)._lastSyncLog = now;
        console.log('📊 [SYNC] Remote video status:', {
          hasStream: !!actualStream,
          streamMatches: actualStream === currentSrcObject,
          videoTracks: actualStream?.getVideoTracks().length || 0,
          playing: videoEl ? !videoEl.paused : false,
          dimensions: videoEl ? `${videoEl.videoWidth}x${videoEl.videoHeight}` : 'N/A'
        });
      }

      // Check if stream reference changed (P2P only - replaceTrack scenarios)
      // SKIP for mediasoup: we handle updates in mediasoup-new-producer; this check causes
      // AbortError loops when multiple producers arrive (audio+video) or sync races with applyRemoteStream
      if (!USE_MEDIASOUP && actualStream && currentSrcObject !== actualStream) {
        console.warn('⚠️ [SYNC MONITOR] Remote stream reference changed - updating video element');
        remoteVideoRef.current!.srcObject = actualStream;
        remoteVideoRef.current!.play().catch((e) => {
          console.error('❌ [SYNC MONITOR] Play failed after stream sync:', e);
        });
        return;
      }

      // Check if video track changed (replaceTrack scenario)
      if (actualStream) {
        const videoTracks = actualStream.getVideoTracks();
        if (videoTracks.length === 0) {
          console.warn('⚠️ [SYNC MONITOR] No video tracks in stream');
          return;
        }

        const currentTrackId = videoTracks[0].id;
        const previousTrackId = previousRemoteVideoTrackIdRef.current;
        const currentTrack = videoTracks[0];

        // Detect track change by comparing with stored previous ID
        // BUT only refresh if track is actually enabled and ready
        if (previousTrackId && previousTrackId !== currentTrackId) {
          const isScreenShare = currentTrack.label?.toLowerCase().includes('screen');
          console.log(`🔄 [SYNC MONITOR] Remote video track changed (guest ${isScreenShare ? 'started screen sharing' : 'switched to camera'}):`, {
            oldTrackId: previousTrackId,
            newTrackId: currentTrackId,
            trackLabel: currentTrack.label, // e.g., "screen:0:0" or "camera 1"
            trackEnabled: currentTrack.enabled,
            trackReadyState: currentTrack.readyState,
            trackMuted: currentTrack.muted,
            isScreenShare
          });

          if (currentTrack.enabled && currentTrack.readyState === 'live') {
            // ALWAYS refresh when track changes - video can report "playing" but show frozen frame
            if (videoEl) {
              console.warn('🔧 [SYNC MONITOR] Track changed - forcing refresh:', {
                paused: videoEl.paused,
                readyState: videoEl.readyState,
                readyStateText: ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][videoEl.readyState]
              });

              // ✅ FIX: Use gentle refresh that doesn't null out srcObject
              // Nulling srcObject causes the recording canvas to lose its video source
              // Instead, just force a play() call which picks up the new track
              const attemptPlay = async () => {
                try {
                  videoEl.playsInline = true;
                  await videoEl.play();
                  console.log('✅ [SYNC MONITOR] Video playing after track change (gentle refresh)');
                } catch (e: any) {
                  console.warn('⚠️ [SYNC MONITOR] Gentle play failed, trying full reset...', e.message);
                  // Only do full reset as last resort
                  const tempSrcObject = videoEl.srcObject;
                  videoEl.srcObject = null;
                  await new Promise(resolve => setTimeout(resolve, 50));
                  if (remoteVideoRef.current) {
                    remoteVideoRef.current.srcObject = tempSrcObject;
                    remoteVideoRef.current.playsInline = true;
                    try {
                      await remoteVideoRef.current.play();
                      console.log('✅ [SYNC MONITOR] Video playing after full reset');
                    } catch (e2: any) {
                      console.warn('⚠️ [SYNC MONITOR] Play failed, retrying muted...', e2.message);
                      try {
                        remoteVideoRef.current.muted = true;
                        await remoteVideoRef.current.play();
                        console.log('✅ [SYNC MONITOR] Video playing muted after track change');
                        // Only unmute if AudioContext is NOT handling audio
                        if (!remoteAudioCtxRef.current) {
                          setTimeout(() => {
                            if (remoteVideoRef.current) {
                              remoteVideoRef.current.muted = false;
                            }
                          }, 500);
                        }
                      } catch (e3) {
                        console.error('❌ [SYNC MONITOR] Failed to play video even muted:', e3);
                      }
                    }
                  }
                }
              };
              attemptPlay();
            }
          } else {
            console.warn('⚠️ [SYNC MONITOR] Track changed but not ready:', {
              enabled: currentTrack.enabled,
              readyState: currentTrack.readyState
            });
          }
        }

        // Always update stored track ID for next iteration
        previousRemoteVideoTrackIdRef.current = currentTrackId;
      }
    }, 500); // Check every 500ms

    return () => {
      console.log('🧹 Stopping remote stream sync monitor');
      clearInterval(syncInterval);
    };
  }, [remoteConnectedState, remoteVideoRef]);

  // ✅ FIX: Retry video playback when remoteConnected state changes
  // This handles cases where video element was hidden during initial connection
  useEffect(() => {
    if (!remoteConnectedState || !remoteVideoRef.current) return;

    const videoEl = remoteVideoRef.current;
    const stream = videoEl.srcObject as MediaStream;

    // Check if video is not playing but has a valid stream
    if (stream && videoEl.paused) {
      console.log('🔄 [REMOTE CONNECTED] Remote connected state changed to true, retrying video playback');

      // Force retry playback
      const retryPlayback = async () => {
        try {
          await videoEl.play();
          console.log('✅ [REMOTE CONNECTED] Video playback started successfully after state change');
        } catch (err: any) {
          console.error('❌ [REMOTE CONNECTED] Video playback failed after state change:', err.message);

          // Try refreshing the stream if playback fails
          if (videoPlaybackServiceRef.current && stream) {
            console.log('🔄 [REMOTE CONNECTED] Attempting stream refresh via VideoPlaybackService');
            try {
              await videoPlaybackServiceRef.current.refreshStream(videoEl, stream, platform);
              console.log('✅ [REMOTE CONNECTED] Stream refresh successful');
            } catch (refreshErr) {
              console.error('❌ [REMOTE CONNECTED] Stream refresh failed:', refreshErr);
            }
          }
        }
      };

      // Small delay to ensure DOM is updated
      setTimeout(retryPlayback, 100);
    }
  }, [remoteConnectedState, remoteVideoRef, platform]);

  const toggleMute = useCallback(() => {
    const webrtcService = webrtcServiceRef.current;
    const signalingService = signalingServiceRef.current;

    if (webrtcService) {
      const isMuted = webrtcService.toggleMute();
      setIsMuted(isMuted);
      console.log(`🎤 Mic ${isMuted ? 'muted' : 'unmuted'}`);

      // ✅ FIX: Notify peer about audio state change
      // This ensures the peer knows about mute state for UI updates
      const socket = (signalingService as any)?.socket;
      if (socket?.connected) {
        socket.emit('audio-state', {
          roomId,
          enabled: !isMuted,
          senderId: socket.id,
        });
        console.log(`📡 Emitted audio-state: enabled=${!isMuted}`);
      }
    } else {
      console.warn('⚠️ WebRTC service not initialized');
    }
  }, [setIsMuted, roomId]);

  const toggleVideo = useCallback(() => {
    const webrtcService = webrtcServiceRef.current;
    const signalingService = signalingServiceRef.current;

    if (webrtcService && signalingService) {
      const isVideoEnabled = webrtcService.toggleVideo(); // Returns true if video is enabled
      const isVideoOff = !isVideoEnabled; // Invert for UI state
      setIsVideoOff(isVideoOff);
      console.log(`📹 Camera ${isVideoOff ? 'OFF' : 'ON'}, enabled=${isVideoEnabled}`);

      // Notify peer about video state change
      const socket = (signalingService as any).socket;
      if (socket && socket.connected) {
        const payload = {
          roomId,
          enabled: isVideoEnabled,
          senderId: socket.id,
        };
        console.log(`📡 Emitting video-state:`, payload);
        socket.emit('video-state', payload);
      } else {
        console.error('⚠️ Socket not connected, cannot emit video-state');
      }
    } else {
      console.warn('⚠️ Services not initialized');
    }
  }, [setIsVideoOff, roomId]);

  const handleEndCall = async () => {
    console.log('📞 Ending call...');

    const webrtcService = webrtcServiceRef.current;
    const signalingService = signalingServiceRef.current;

    // ✅ Stop all local media tracks (camera, microphone)
    if (webrtcService) {
      webrtcService.stopLocalStream();
    }

    // ✅ Clear local stream reference
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`🛑 Stopped local ${track.kind} track`);
      });
      localStreamRef.current = null;
    }

    // ✅ Clear video element srcObjects to fully release media devices
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
      console.log('🧹 Cleared local video srcObject');
    }

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
      console.log('🧹 Cleared remote video srcObject');
    }

    if (signalingService) {
      try {
        await signalingService.leaveRoom();
        console.log('✅ Left room');
      } catch (e) {
        console.error("⚠️ Leave-room failed:", e);
      }
    }

    if (webrtcService) {
      webrtcService.closePeerConnection();
    }

    setRemoteConnectedState(false);
    setRemoteConnected(false);
    setConnectionStatus("peer-left");
    console.log('✅ Call ended - all media devices released');
  };

  const handleTapToPlay = useCallback(async () => {
    console.log('👆 👆 👆 User tapped to play videos');

    // ✅ iOS Audio Fix: Resume AudioContext during this user gesture
    if (remoteAudioCtxRef.current && remoteAudioCtxRef.current.state === 'suspended') {
      try {
        await remoteAudioCtxRef.current.resume();
        console.log('🔊 [iOS Audio] AudioContext resumed via user tap, state:', remoteAudioCtxRef.current.state);
      } catch (e) {
        console.warn('⚠️ [iOS Audio] Failed to resume AudioContext:', e);
      }
    }

    // Try to play both local and remote videos
    const videos = [
      { ref: localVideoRef.current, name: 'local' },
      { ref: remoteVideoRef.current, name: 'remote' }
    ].filter(v => v.ref);

    for (const { ref: video, name } of videos) {
      if (video && video.srcObject) {
        console.log(`🎬 Attempting to play ${name} video:`, {
          srcObject: !!video.srcObject,
          paused: video.paused,
          readyState: video.readyState,
          currentTime: video.currentTime,
          streamTracks: (video.srcObject as MediaStream)?.getTracks().map(t => ({
            kind: t.kind,
            id: t.id,
            label: t.label,
            enabled: t.enabled,
            readyState: t.readyState
          }))
        });

        try {
          // If video element is stuck in readyState 0, force a reset
          if (video.readyState === 0 && video.srcObject) {
            console.warn(`⚠️ ${name} video stuck in readyState 0, forcing reset...`);
            const stream = video.srcObject;
            video.pause();
            video.srcObject = null;
            await new Promise(resolve => setTimeout(resolve, 100));
            video.srcObject = stream;
            console.log(`🔄 ${name} video reset complete, waiting for metadata...`);

            // Wait for metadata
            await new Promise<void>((resolve) => {
              if (video.readyState >= 1) {
                resolve();
              } else {
                const onLoadedMetadata = () => {
                  console.log(`📺 ${name} video metadata loaded after reset`);
                  video.removeEventListener('loadedmetadata', onLoadedMetadata);
                  resolve();
                };
                video.addEventListener('loadedmetadata', onLoadedMetadata);
                setTimeout(() => {
                  video.removeEventListener('loadedmetadata', onLoadedMetadata);
                  resolve();
                }, 2000);
              }
            });
          }

          // Always mute the remote video element — audio goes through GainNode on all platforms.
          video.muted = name === 'local' ? true : !!remoteAudioCtxRef.current;
          video.playsInline = true;

          await video.play();
          console.log(`✅ ${name} video playing after user tap`, {
            paused: video.paused,
            currentTime: video.currentTime,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            readyState: video.readyState
          });
        } catch (e: any) {
          console.error(`❌ ${name} play failed after tap:`, e.message, {
            name: e.name,
            paused: video.paused,
            readyState: video.readyState
          });
        }
      } else {
        console.warn(`⚠️ ${name} video not ready:`, {
          hasElement: !!video,
          hasSrcObject: video?.srcObject
        });
      }
    }

  }, [localVideoRef, remoteVideoRef, platform.isIOS, platform.isWebView]);

  // Mute/unmute the WebRTC remote audio (used when translation is active to avoid double audio)
  const setRemoteAudioMuted = useCallback((muted: boolean) => {
    remoteAudioMutedRef.current = muted; // persist so GainNode creation sites pick it up
    // Audio always routes through GainNode (AudioContext created eagerly on mount).
    // Do NOT touch remoteVideoRef.current.muted — the video element is permanently muted
    // (muted = !!remoteAudioCtxRef.current = true) and audio comes through GainNode only.
    // Touching .muted here would fight with the stream setup code and break normal audio.
    if (remoteAudioGainRef.current) {
      remoteAudioGainRef.current.gain.value = muted ? 0 : 1;
    }
    console.log(`[WebRTC] Remote audio ${muted ? 'muted' : 'unmuted'} for translation`);
  }, []);

  return {
    connectionStatus,
    remoteConnected: remoteConnectedState,
    remoteVideoEnabled,
    waitingForHost,
    remoteVideoMuted: (platform.isIOS || platform.isWebView) && remoteAudioCtxRef.current?.state === 'running',
    setRemoteAudioMuted,
    startCall,
    handleEndCall,
    toggleMute,
    toggleVideo,
    cleanupRemote,
    handleTapToPlay,
    signalingServiceRef,
    webrtcServiceRef, // ✅ Expose for accessing remote stream in transcription
  };
}
